import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import appDisplay from 'scoreboard/components/app-display'
import DeleteConfirmation from 'scoreboard/components/delete-confirmation'
import guard from 'scoreboard/components/unsaved/guard'
import i18n from 'scoreboard/components/i18n'
import LoadingDots from 'scoreboard/components/loading-dots'
import selects from 'scoreboard/components/selects'
import Tooltip from 'scoreboard/components/tooltip'
import AdminManageDatasetTreesDialog from './admin-manage-rollup-trees-dialog'
import { CHANGE_ITEM_EVENT } from '../admin-two-column-editor'
import DatasetTree from './rollup-tree'
import DatasetsTreeEditorMenu from './rollup-tree-editor-menu'
import DatasetTreeGardener from 'scoreboard/datasets/dataset-tree-gardener'
import template from './rollup-tree-editor-selector.hbs'

/**
 * Contains a select with all the rollup trees and the Dataset Rollup Tree
 * Gardener for the currently selected tree.
 *
 * User can modify the tree here (add, move, delete) in addition to making
 * a tree selection that will show the rollupTreeGroups and keysets for the tree.
 *
 *
 * @property {DatasetTreeGardener} gardener
 */
var RollupTreeEditorSelector = Backbone.View.extend({

	className: 'datasets-tree-editor-selector panel left-panel',
	headingKey: 'datasets.dataType.rollupTrees',
	searchMessageKey: 'datasets.trees.search',

	events: {
		'change .rollup-tree': 'onChangeRollupTree',
		'click .manage-rollup-trees': 'onClickManageRollupTrees'
	},

	/**
	 *
	 * @param {Number} [options.itemId] The tree that should be selected,
	 *     provided by admin-two-column-editor.js
	 */
	initialize: function(options = {}) {
		this.options = options
	},

	render: function() {
		this.$el.empty().append(template({
			emptyMessage: i18n.get('common.thereAreNo', 'datasets.trees')
		}))
		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })
		this.loadingDots.render()
		this._renderGardener()

		Backbone.on('add:dataset-tree', ({id}) => {
			this.selectedTreeId = id
		})

		return this
	},

	_hasTrees() {
		return !_.isNil(this.trees) && this.trees.length > 0
	},

	_renderGardener() {
		if (_.isNil(this.gardener)) {
			this.gardener = new DatasetTreeGardener({
				readyToStream: false,
				finderOptions: {
					type: 'light',
					autofocus: true
				},
				initialSelection: this.options.itemId,
				showKeys: true,
				editable: true,
				lightTree: true
			})
			this.$('.panel-content').append(this.gardener.el)
			this.gardener.render()
			this.$('.min-height-vertical-center').addClass('is-not-displayed')

			// Bind events (once)
			this.gardener.on('select', treeNode => this._onRollupTreeSelect(treeNode))
			this.gardener.on('tree:rendered', () => {
				// As we add and delete rollup trees, we're creating and re-creating
				// the gardener based on whether there are any trees or not, so
				// make sure we have a tree root before proceeding here.
				// #53588 and manageTreesDialog should be closed
				if (this.gardener.tree.root && this.initialSelection && _.isNil(this.manageTreesDialog)) {
					this._loadTreeSelection(this.gardener.tree.root)
				}
				this.gardener._updateOptions({ initialSelection: null })
				this.initialSelection = true
			})

			// hide the gardener and body until a tree is selected or
			// the no trees message is shown
			this.$('.gardener').addClass('is-not-displayed')
			this.$el.addClass('hide-body')

		} else if (!this._hasTrees()) {
			// when there are no trees, show the body with the no trees message
			this.$el.removeClass('hide-body')
			_.invoke(this.gardener, 'remove')
			delete this.gardener
			this.$('.min-height-vertical-center').removeClass('is-not-displayed')
			this.trigger('remove:gardener')
		}
	},

	_renderFormOptions() {
		this.rollupTreeSelect = this.$('.rollup-tree')

		if (this._hasTrees()) {
			// remove the existing select and create a new one
			if (this.rollupTreeSelect.length > 0) {
				this.rollupTreeSelect.remove()
			}

			// Must ignore changes here, so selectting a tree doesn't dirty the form;
			// we don't want a guard if the user didn't change anything in the right column
			this.$('.form-options').append(`
				<select name="rollupTree" class="rollup-tree is-ignoring-change"></select>
			`)

			// #34940 now that the rollup tree a filtering select, we need to
			// make sure that first tree is selected by default when you first
			// visit the screen
			let firstTreeId = this._firstTreeId()

			this.rollupTreeSelect = this.$('.rollup-tree')
			const options = _.map(this.trees, t => ({
				value: t.id,
				label: t.treeLabel
			}))
			selects.filtering(this.rollupTreeSelect, {
				options,
				sort: [], // maintain order specified via "Manage Rollup Trees"
				placeholder: i18n.get('datasets.trees.choose')
			})

			// auto select a tree if there is only one tree
			// or if the user is viewing a specific tree
			let rootId = this.options.rootId
			if (this.trees.length === 1 || !_.isNil(rootId)) {
				let selectedId = rootId || firstTreeId
				selects.val(this.rollupTreeSelect, selectedId)
			}
		} else {
			this.rollupTreeSelect.addClass('is-not-displayed')
		}

		this.$('.form-options').toggleClass('has-rollup-tree-select', this._hasTrees())
	},

	_firstTreeId() {
		return _.get(_.first(this.trees), 'id')
	},

	/**
	 * Loads all of the rollup tree options
	 */
	setItems: function(trees) {
		this.loadingDots.remove()
		this.$('.panel-content').removeClass('is-not-displayed')
		this.trees = trees

		// If the currently selected tree is deleted automatically move
		// the selection to the first tree (#35028)
		if (this.selectedTreeId &&
			!_.some(trees, t => t.id == this.selectedTreeId)) {
			this.selectedTreeId = this._firstTreeId()
		}

		// select is already set to the correct selection; short-circuit
		if (this.selectedTreeId &&
			selects.val(this.rollupTreeSelect) == this.selectedTreeId) {
			return
		}

		this._renderGardener()
		this._renderFormOptions()
		if (this.selectedTreeId) {
			// Must apply new tree selection here (after it is added to the options)
			selects.val(this.rollupTreeSelect, this.selectedTreeId)
		}
		// Initial load or as a result of manage trees dialog
		// Modifying trees will dirty the form; must be ignored
		this.onChangeRollupTree({noGuard: true})
	},

	/**
	 * The user has clicked on a node in the rollup tree gardener; guard and load.
	 */
	_onRollupTreeSelect: function(treeNode = {}) {
		guard.conditionallyRun(() => this._loadTreeSelection(treeNode))
	},

	/**
	 * Reload the right column view for a tree node
	 */
	_loadTreeSelection(treeNode) {
		this.trigger(CHANGE_ITEM_EVENT, treeNode)
		this.showTreeMenuTooltip(treeNode)
	},

	// This view's event handlers

	/**
	 * User has selected a different tree in the dropdown.
	 * Reload the gardener and the right panel
	 *
	 * @param [options.noGuard]
	 */
	onChangeRollupTree: function(options = {}) {
		// No trees here, we must have just deleted them all, so skip all that's
		// going on here.
		if (!this._hasTrees()) {
			return
		}

		this.selectedTreeId = selects.val(this.rollupTreeSelect)
		if (_.isNil(this.selectedTreeId) || _.isEmpty(this.selectedTreeId)) {
			return // Can't load a tree that doesn't exist (empty select)
		}
		const previous = selects.previousValue(this.rollupTreeSelect)

		const applySelection = () => {
			this.gardener.insertLoadingDots()
			this.gardener.show({id: this.selectedTreeId, force: true})
			this.gardener.once('node', () => {

				// show the gardener and body upon tree selection
				this.$el.removeClass('hide-body')
				this.$('.gardener').removeClass('is-not-displayed')

				let newlySelected = this.gardener.tree.selected()
				if (newlySelected && _.isNil(this.manageTreesDialog)) {
					this._loadTreeSelection(newlySelected)
				}
			})
		}

		if (options.noGuard) {
			applySelection()
		} else {
			guard.conditionally({
				continueFunction: applySelection,
				// Must restore the previous selection since we can't stop the
				// event from propagating in time for it to no apply the new value
				cancelFunction: () => selects.val(this.rollupTreeSelect, previous)
			})
		}
	},

	onClickManageRollupTrees: function() {
		guard.conditionallyRun(() => {
			this.manageTreesDialog = new AdminManageDatasetTreesDialog({
				collection: this.trees
			})
			this.manageTreesDialog
				.on('change', items => this.setItems(items))
				// #53588 Wait until upload dialog is closed and then render tooltip
				.on('remove', items => {
					if (!_.isNil(this.gardener?.tree)) {
						this._loadTreeSelection(this.gardener.tree.root)
					}
				})
				.on('replace', tree => {
					// #58324 if the currently selected tree was just replaced,
					// rerender the gardener so the updated tree shows
					if (tree.id == this.selectedTreeId) {
						const editable = this.gardener.editable()
						this.gardener.reload()

						// ensure that the gardener maintains its
						// editable/non-editable state
						this.gardener.editable(editable)
					}
				})
			appDisplay.dialog(this.manageTreesDialog)
		})
	},

	// creates the delete confirmation prompt displayed to the user
	_createDeleteConfirmation: function() {
		// #8972 don't clobber the title of the delete dialog
		var options = _.omit(this.item.attributes, 'title')
		options.label = this.item.get('treeLabel')
		return new DeleteConfirmation(options)
	},

	showTreeMenuTooltip: function(node) {
		if (!this.gardener.tree.isEditable() ||
			_.isNil(this.gardener.tree.selected())) {
			return
		}
		this.$el.off('transitionend', this.menuTooltipDoneHandler)
		_.invoke([this.menuTooltip, this.editorMenu], 'remove')
		this.showEditorMenu(node)
		this.menuTooltip = new Tooltip({
			content: this.editorMenu,
			attachTo: $(this.gardener.tree.selectedEl()),
			placement: 'right',
			arrow: 'top',
			extraClass: 'datasets-tree-editor-menu tree-tooltip tree-tooltip-light'
		}).render()
		// #13584 re-display the tooltip after adding or deleting a tree item
		this.listenToOnce(this.editorMenu, 'done', () => {
			// Wait for the dialog to scale back to it's 100% size before attaching the tooltip;
			// otherwise, the position will be miscalculated. Issue #30819
			this.menuTooltipDoneHandler = () => {
				this.showTreeMenuTooltip(node)
			}
			this.$el.one('transitionend', this.menuTooltipDoneHandler)
		})
	},

	showEditorMenu: function(node) {
		this.editorMenu = new DatasetsTreeEditorMenu({
			model: new DatasetTree(node)
		})
		this.editorMenu.on('delete', id => {
			this.trees = _.filter(this.trees, tree => {
				return tree.id != id
			})
			this.setItems(this.trees)
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this.gardener,
			this.menuTooltip, this.editorMenu], 'remove')
	}

})

export default RollupTreeEditorSelector

RollupTreeEditorSelector.__filename__ = 'admin/datasets/rollup-tree-editor-selector.js'
